import { useEffect, useState } from "react";
import { useWizardAnalytics, emailDomain, planRevenue } from "./useWizardAnalytics";
import { getPlans, getProducts, buildPlanApi, getTestMode } from "./api";
import { QuestionnaireStep } from "./steps/QuestionnaireStep";
import { PlanStep } from "./steps/PlanStep";
import { PaymentStep } from "./steps/PaymentStep";
import { Confirmation } from "./components/Confirmation";
import type {
  Answers,
  PaymentResult,
  AccountResult,
  WizardStep,
  PlansResponse,
  SelfServeProduct,
} from "./wizardTypes";
import { bookMeetingUrl } from "./wizardTypes";
import type { QuestionnaireForm } from "./schemas/questionnaireSchema";
import Loading from "src/components/Loading";
import { mixpanelPlgTrack } from "src/services/mixpanelService";
import styles from "./css/SignupWizard.module.css";


type SignupWizardProps = {
  /** When true, wraps the wizard in a full-viewport container (used on standalone /signup page). */
  fullPage?: boolean;
};

export function SignupWizard({ fullPage }: SignupWizardProps = {}) {
  const { state, dispatch, trackPaymentStarted, trackPaymentFailed } = useWizardAnalytics();
  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const [products, setProducts] = useState<SelfServeProduct[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  // Internal IP (office/VPN): show the no-charge test-signup recipe. Server-decided; a miss is silent.
  const [internalIp, setInternalIp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);

  useEffect(() => {
    getTestMode()
      .then((r) => setInternalIp(r.internalIp))
      .catch(() => setInternalIp(false));
  }, []);

  // Fetch the product catalogue and pricing tiers on mount
  useEffect(() => {
    Promise.all([
      getProducts().then(setProducts),
      getPlans().then(setPlans),
    ])
      .catch((err) => console.error("Failed to load catalogue:", err))
      .finally(() => setCatalogLoading(false));
  }, []);

  // Sales clicks bypass the reducer. Anchors pass navigate=false and use their
  // own href; questionnaire submissions open a new tab.
  const handleToSales = (reason: string, email?: string, navigate: boolean = true) => {
    // Plan-step links use the submitted questionnaire email.
    const contact = email ?? state.answers.email;
    const { mrr, arr } = planRevenue(state.plan);
    mixpanelPlgTrack(
      "sales_lead_created",
      {
        reason,
        mrr,
        arr,
        email_domain: emailDomain(contact),
      },
      // Top-window navigation destroys the iframe: bypass batching and use
      // sendBeacon. New-tab navigation leaves the normal batcher running.
      navigate ? undefined : { transport: "sendBeacon", send_immediately: true },
    );
    if (navigate) window.open(bookMeetingUrl(contact), "_blank", "noopener,noreferrer");
  };

  const handleQuestionnaireSubmit = async (answers: Answers) => {
    setPlanError(null);
    setLoading(true);
    try {
      // Needs are already productKey values from the SelfServeProducts table
      const apiNeeds = answers.needs.filter(Boolean);

      // Map volume strings to integers
      const apiVolumes: Record<string, number> = {};
      if (answers.volumes.scrubVolume) {
        const vol = parseInt(answers.volumes.scrubVolume, 10);
        if (!isNaN(vol)) {
          apiVolumes.dncscrub = vol;
          if (apiNeeds.includes("reassigned")) apiVolumes.reassigned = vol;
        }
      }
      if (answers.volumes.callerIdVolume) {
        const vol = parseInt(answers.volumes.callerIdVolume, 10);
        if (!isNaN(vol)) apiVolumes.trustcall = vol;
      }

      const plan = await buildPlanApi(
        apiNeeds,
        apiVolumes,
        answers.email ?? "",
      );
      dispatch({ type: "SUBMIT_QUESTIONNAIRE", answers, plan });
    } catch (err) {
      console.error("Failed to build plan:", err);
      setPlanError("We couldn\u2019t build your plan. Please try again, or contact us if the problem continues.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPlan = () => {
    dispatch({ type: "CONFIRM_PLAN" });
  };

  const handlePaymentSubmit = (paymentResult: PaymentResult, accountResult: AccountResult) => {
    dispatch({ type: "SUBMIT_PAYMENT", payment: paymentResult, account: accountResult });
  };

  const handleGoToStep = (step: WizardStep) => {
    dispatch({ type: "GO_TO_STEP", step });
  };

  const className = fullPage
    ? `${styles.wizard} ${styles.wizardPage}`
    : styles.wizard;

  return (
    <div className={className}>
      {/* Step indicator hidden for now — navigation via step content only */}

      <div className={styles.wizardScroll}>
        <div className={styles.wizardInner}>
          {internalIp && state.step !== "confirmation" && (
            <div className={styles.testModePill} role="note">
              Internal IP detected. For a no-charge <strong>test signup</strong>, use an
              approved internal email address and an NMI test card, such as <code>4111 1111 1111 1111</code>
              {" "}(any future expiry, any CVV). In production, real cards use normal billing.
            </div>
          )}
          {planError && state.step === "questionnaire" && (
            <div className={styles.errorBanner}>{planError}</div>
          )}

          {state.step === "questionnaire" && (
            catalogLoading ? (
              <Loading />
            ) : (
              <QuestionnaireStep
                onSubmit={handleQuestionnaireSubmit}
                onToSales={handleToSales}
                products={products}
                plans={plans}
                loading={loading}
                defaultValues={
                  state.answers.needs.length > 0
                    ? {
                        needs: state.answers.needs as QuestionnaireForm["needs"],
                        scrubVolume: state.answers.volumes.scrubVolume,
                        callerIdVolume: state.answers.volumes.callerIdVolume,
                        email: state.answers.email,
                      }
                    : undefined
                }
              />
            )
          )}

          {state.step === "plan" && state.plan && (
            <PlanStep
              plan={state.plan}
              answers={state.answers}
              onConfirm={handleConfirmPlan}
              onGoToStep={handleGoToStep}
              onToSales={handleToSales}
            />
          )}

          {state.step === "payment" && state.plan && state.answers.email && (
            <PaymentStep
              plan={state.plan}
              email={state.answers.email}
              onSubmit={handlePaymentSubmit}
              onGoToStep={handleGoToStep}
              onPaymentStarted={trackPaymentStarted}
              onPaymentFailed={trackPaymentFailed}
            />
          )}

          {state.step === "confirmation" && <Confirmation state={state} />}
        </div>
      </div>
    </div>
  );
}
